import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { complianceAPI, documentsAPI, documentTypesAPI, notificationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Search,
  RefreshCw,
  Eye,
  Download,
  ThumbsUp,
  ThumbsDown,
  Shield,
  Upload,
  Plus,
  X,
  Mail,
  History,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, addMonths } from 'date-fns';
import { STATUS_STYLES, COMPLIANCE_STYLES, ACTIVATION_STYLES } from '../components/compliance/constants';
import { DocumentHistoryModal } from '../components/compliance/DocumentHistoryModal';
import { DocumentReviewModal } from '../components/compliance/DocumentReviewModal';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import { usePermissions } from '../hooks/usePermissions';

export default function Compliance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canApproveDocuments, canExportCSV, canSendReminders, canUploadDocsForOthers } = usePermissions();
  
  // Initialize activeTab based on URL filter - if filtering by status, show medics tab
  const [activeTab, setActiveTab] = useState(() => {
    const urlFilter = searchParams.get('filter');
    return urlFilter ? 'medics' : 'pending';
  });
  const [pendingDocs, setPendingDocs] = useState([]);
  const [medics, setMedics] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedMedic, setSelectedMedic] = useState(null);
  const [showMedicModal, setShowMedicModal] = useState(false);
  const [medicDocuments, setMedicDocuments] = useState([]);
  // Initialize status filter from URL params (e.g., ?filter=expiring_soon or ?filter=expired)
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlFilter = searchParams.get('filter');
    return urlFilter || 'all';
  });
  
  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    document_type_id: '',
    expires_at: '',
    use_default_expiry: true
  });
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null); // Track which medic is being sent a reminder
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [documentHistory, setDocumentHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Inline document preview (iter71.3 — Ops Coord inline review).
  const [previewDoc, setPreviewDoc] = useState(null);

  // Re-categorise (retype) document state
  const [showRetypeModal, setShowRetypeModal] = useState(false);
  const [retypeDoc, setRetypeDoc] = useState(null);
  const [retypeTargetId, setRetypeTargetId] = useState('');
  const [retyping, setRetyping] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, medicsRes, docTypesRes] = await Promise.all([
        documentsAPI.getPending(),
        complianceAPI.getMedics(),
        documentTypesAPI.list().catch(() => ({ data: [] }))
      ]);
      setPendingDocs(pendingRes.data);
      setMedics(medicsRes.data);
      setDocumentTypes(docTypesRes.data);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const response = await complianceAPI.exportCSV();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Compliance report exported');
    } catch (error) {
      toast.error('Failed to export compliance report');
    } finally {
      setExporting(false);
    }
  };

  const handleSendReminder = async (medic) => {
    if (!medic.email) {
      toast.error('Medic has no email address');
      return;
    }
    
    try {
      setSendingReminder(medic.id);
      const response = await notificationsAPI.sendReminder(medic.id);
      
      if (response.data.status === 'no_action') {
        toast.info(response.data.message);
      } else {
        toast.success(`Reminder sent to ${medic.name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleViewHistory = async (doc) => {
    try {
      setLoadingHistory(true);
      setShowHistoryModal(true);
      const response = await documentsAPI.getHistory(doc.id);
      setDocumentHistory(response.data);
    } catch (error) {
      toast.error('Failed to load document history');
      setShowHistoryModal(false);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openRetypeModal = (doc) => {
    setRetypeDoc(doc);
    setRetypeTargetId(doc.document_type_id || '');
    setShowRetypeModal(true);
  };

  const handleRetypeDocument = async () => {
    if (!retypeDoc || !retypeTargetId) return;
    if (retypeTargetId === retypeDoc.document_type_id) {
      toast.info('Document is already this type');
      return;
    }
    try {
      setRetyping(true);
      await documentsAPI.retype(retypeDoc.id, retypeTargetId);
      toast.success('Document re-categorised');
      setShowRetypeModal(false);
      setRetypeDoc(null);
      setRetypeTargetId('');
      // Refresh medic documents + main data
      if (selectedMedic) {
        const res = await documentsAPI.getByMedic(selectedMedic.id);
        setMedicDocuments(res.data);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-categorise document');
    } finally {
      setRetyping(false);
    }
  };

  const handleReviewDocument = async (approved) => {
    if (!selectedDoc) return;
    
    try {
      setProcessing(true);
      await documentsAPI.review(selectedDoc.id, {
        approved,
        notes: reviewNotes,
        rejection_reason: approved ? null : rejectionReason
      });
      toast.success(approved ? 'Document approved' : 'Document rejected');
      setShowReviewModal(false);
      setSelectedDoc(null);
      setReviewNotes('');
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review document');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const openReviewModal = (doc) => {
    setSelectedDoc(doc);
    setReviewNotes('');
    setRejectionReason('');
    setShowReviewModal(true);
  };

  const viewMedicDetails = async (medic) => {
    try {
      const res = await documentsAPI.getByMedic(medic.id);
      setMedicDocuments(res.data);
      setSelectedMedic(medic);
      setShowMedicModal(true);
      // Reset upload form
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadForm({ document_type_id: '', expires_at: '', use_default_expiry: true });
    } catch (error) {
      toast.error('Failed to load medic documents');
    }
  };

  // File dropzone for upload
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setUploadFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  // Calculate expiry date based on document type's default
  const handleDocTypeChange = (typeId) => {
    setUploadForm(prev => ({ ...prev, document_type_id: typeId }));
    
    if (uploadForm.use_default_expiry) {
      const selectedType = documentTypes.find(t => t.id === typeId);
      if (selectedType?.default_expiry_months) {
        const defaultExpiry = addMonths(new Date(), selectedType.default_expiry_months);
        setUploadForm(prev => ({ 
          ...prev, 
          document_type_id: typeId,
          expires_at: format(defaultExpiry, 'yyyy-MM-dd')
        }));
      } else {
        setUploadForm(prev => ({ ...prev, document_type_id: typeId, expires_at: '' }));
      }
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadForm.document_type_id || !selectedMedic) {
      toast.error('Please select a document type and file');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('medic_id', selectedMedic.id);
      formData.append('document_type_id', uploadForm.document_type_id);
      if (uploadForm.expires_at) {
        formData.append('expires_at', uploadForm.expires_at);
      }

      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      
      // Refresh medic documents
      const res = await documentsAPI.getByMedic(selectedMedic.id);
      setMedicDocuments(res.data);
      
      // Reset upload form
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadForm({ document_type_id: '', expires_at: '', use_default_expiry: true });
      
      // Refresh main data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const filteredMedics = medics.filter(medic => {
    const matchesSearch = medic.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          medic.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                          medic.activation_status === statusFilter ||
                          medic.compliance_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1ABC9C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#F2F2F2]">Compliance Management</h1>
        <Button onClick={fetchData} variant="outline" className="border-white/10">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/15">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Pending Review</p>
                <p className="text-xl font-bold text-yellow-400">{pendingDocs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Active Medics</p>
                <p className="text-xl font-bold text-[#1ABC9C]">
                  {medics.filter(m => m.activation_status === 'active').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/15">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Expiring Soon</p>
                <p className="text-xl font-bold text-yellow-400">
                  {medics.filter(m => m.compliance_status === 'expiring_soon').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/15">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Non-Compliant</p>
                <p className="text-xl font-bold text-red-400">
                  {medics.filter(m => m.compliance_status === 'expired' || !m.can_activate).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#2B2B2B]">
          <TabsTrigger value="pending" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white">
            <Clock className="w-4 h-4 mr-2" />
            Pending Review ({pendingDocs.length})
          </TabsTrigger>
          <TabsTrigger value="medics" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            All Medics
          </TabsTrigger>
        </TabsList>

        {/* Pending Documents Tab */}
        <TabsContent value="pending" className="mt-6">
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                <FileText className="w-5 h-5 text-yellow-400" />
                Documents Awaiting Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingDocs.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50 text-[#1ABC9C]" />
                  <p className="text-lg">All documents reviewed!</p>
                  <p className="text-sm mt-1">No pending documents at this time</p>
                </div>
              ) : (
                <>
                <Table className="hidden lg:table">
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                      <TableHead className="text-[#A1A1AA]">Document Type</TableHead>
                      <TableHead className="text-[#A1A1AA]">File</TableHead>
                      <TableHead className="text-[#A1A1AA]">Uploaded</TableHead>
                      <TableHead className="text-[#A1A1AA]">Expires</TableHead>
                      <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingDocs.map(doc => (
                      <TableRow key={doc.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-[#F2F2F2]">
                          <div>
                            <p className="font-medium">{doc.medic_name}</p>
                            <p className="text-sm text-[#A1A1AA]">{doc.medic_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#F2F2F2]">{doc.document_type_name}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{doc.file_name}</TableCell>
                        <TableCell className="text-[#A1A1AA]">
                          {format(parseISO(doc.created_at), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell className="text-[#A1A1AA]">
                          {doc.expires_at 
                            ? format(parseISO(doc.expires_at), 'dd/MM/yyyy')
                            : 'No expiry'
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                              title="Preview"
                              data-testid={`pending-doc-preview-${doc.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownload(doc)}
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {canApproveDocuments && (
                              <Button 
                                size="sm"
                                onClick={() => openReviewModal(doc)}
                                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Review
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pending Documents — mobile cards */}
                <div className="lg:hidden space-y-3" data-testid="pending-docs-mobile">
                  {pendingDocs.map(doc => (
                    <div key={doc.id} className="p-3 bg-[#1E1E1E] rounded-lg space-y-2 border border-white/5" data-testid={`pending-doc-card-${doc.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[#F2F2F2] font-medium truncate">{doc.medic_name}</p>
                          <p className="text-xs text-[#A1A1AA] truncate">{doc.medic_email}</p>
                        </div>
                        <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-500/20 shrink-0 text-xs">
                          Pending
                        </Badge>
                      </div>
                      <div className="text-sm text-[#F2F2F2]">{doc.document_type_name}</div>
                      <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
                        <span className="truncate">{doc.file_name}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
                        <span>Uploaded {format(parseISO(doc.created_at), 'dd/MM/yyyy')}</span>
                        <span>
                          {doc.expires_at
                            ? `Expires ${format(parseISO(doc.expires_at), 'dd/MM/yyyy')}`
                            : 'No expiry'}
                        </span>
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewDoc(doc)}
                          className="h-8"
                          title="Preview"
                          data-testid={`pending-doc-preview-mobile-${doc.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          className="h-8"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {canApproveDocuments && (
                          <Button
                            size="sm"
                            onClick={() => openReviewModal(doc)}
                            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 h-8"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Medics Tab */}
        <TabsContent value="medics" className="mt-6">
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <CardTitle className="text-[#F2F2F2]">Medic Compliance Overview</CardTitle>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                  <Input
                    placeholder="Search medics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-[#1E1E1E] border-white/10 w-full sm:w-64"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="flex-1 sm:flex-none bg-[#1E1E1E] border border-white/10 rounded-md px-3 py-2 text-[#F2F2F2] text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                    <option value="expiring_soon">Expiring Soon</option>
                    <option value="expired">Expired</option>
                  </select>
                  {canExportCSV && (
                    <Button
                      onClick={handleExportCSV}
                      disabled={exporting}
                      variant="outline"
                      className="flex-1 sm:flex-none border-white/10 text-[#F2F2F2] hover:bg-white/5"
                      data-testid="export-csv-btn"
                    >
                      {exporting ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export CSV
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table className="hidden lg:table">
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                    <TableHead className="text-[#A1A1AA]">Activation</TableHead>
                    <TableHead className="text-[#A1A1AA]">Compliance</TableHead>
                    <TableHead className="text-[#A1A1AA]">Progress</TableHead>
                    <TableHead className="text-[#A1A1AA]">Docs Status</TableHead>
                    <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMedics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-[#A1A1AA] py-8">
                        No medics found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMedics.map(medic => (
                      <TableRow key={medic.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-[#F2F2F2]">
                          <div>
                            <p className="font-medium">{medic.name}</p>
                            <p className="text-sm text-[#A1A1AA]">{medic.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={ACTIVATION_STYLES[medic.activation_status || 'pending']}>
                            {(medic.activation_status || 'pending').replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={COMPLIANCE_STYLES[medic.compliance_status || 'pending']}>
                            {(medic.compliance_status || 'pending').replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="w-32">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-[#A1A1AA]">Compliance</span>
                              <span className="text-[#F2F2F2]">{medic.compliance_percentage || 0}%</span>
                            </div>
                            <Progress value={medic.compliance_percentage || 0} className="h-2" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            {medic.missing_docs_count > 0 && (
                              <span className="text-red-400">{medic.missing_docs_count} missing</span>
                            )}
                            {medic.pending_docs_count > 0 && (
                              <span className="text-yellow-400">{medic.pending_docs_count} pending</span>
                            )}
                            {medic.missing_docs_count === 0 && medic.pending_docs_count === 0 && (
                              <span className="text-[#1ABC9C]">Complete</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canSendReminders && (medic.missing_docs_count > 0 || medic.compliance_status === 'expiring_soon') && medic.email && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleSendReminder(medic)}
                                disabled={sendingReminder === medic.id}
                                className="border-[#1ABC9C]/30 text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
                                data-testid={`send-reminder-${medic.id}`}
                              >
                                {sendingReminder === medic.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Mail className="w-4 h-4 mr-1" />
                                    Remind
                                  </>
                                )}
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => viewMedicDetails(medic)}
                              className="border-white/10"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* All Medics — mobile cards */}
              <div className="lg:hidden space-y-3" data-testid="compliance-medics-mobile">
                {filteredMedics.length === 0 ? (
                  <div className="text-center text-[#A1A1AA] py-8">No medics found</div>
                ) : (
                  filteredMedics.map(medic => (
                    <div key={medic.id} className="p-3 bg-[#1E1E1E] rounded-lg space-y-3 border border-white/5" data-testid={`compliance-medic-card-${medic.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[#F2F2F2] font-medium truncate">{medic.name}</p>
                          <p className="text-xs text-[#A1A1AA] truncate">{medic.email}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge className={ACTIVATION_STYLES[medic.activation_status || 'pending']}>
                            {(medic.activation_status || 'pending').replace('_', ' ')}
                          </Badge>
                          <Badge className={COMPLIANCE_STYLES[medic.compliance_status || 'pending']}>
                            {(medic.compliance_status || 'pending').replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-[#A1A1AA]">Compliance</span>
                          <span className="text-[#F2F2F2]">{medic.compliance_percentage || 0}%</span>
                        </div>
                        <Progress value={medic.compliance_percentage || 0} className="h-2" />
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {medic.missing_docs_count > 0 && (
                          <span className="text-red-400">{medic.missing_docs_count} missing</span>
                        )}
                        {medic.pending_docs_count > 0 && (
                          <span className="text-yellow-400">{medic.pending_docs_count} pending</span>
                        )}
                        {medic.missing_docs_count === 0 && medic.pending_docs_count === 0 && (
                          <span className="text-[#1ABC9C]">Complete</span>
                        )}
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                        {canSendReminders && (medic.missing_docs_count > 0 || medic.compliance_status === 'expiring_soon') && medic.email && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendReminder(medic)}
                            disabled={sendingReminder === medic.id}
                            className="border-[#1ABC9C]/30 text-[#1ABC9C] hover:bg-[#1ABC9C]/10 h-8"
                          >
                            {sendingReminder === medic.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Mail className="w-4 h-4 mr-1" />
                                Remind
                              </>
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => viewMedicDetails(medic)}
                          className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 h-8"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Document Modal */}
      <DocumentReviewModal
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        document={selectedDoc}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        processing={processing}
        onReview={handleReviewDocument}
      />

      {/* Medic Details Modal */}
      <Dialog open={showMedicModal} onOpenChange={setShowMedicModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-[#1ABC9C]" />
              {selectedMedic?.name} - Compliance Details
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              View compliance status, upload documents, and review pending submissions.
            </DialogDescription>
          </DialogHeader>
          {selectedMedic && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={ACTIVATION_STYLES[selectedMedic.activation_status || 'pending']}>
                  {(selectedMedic.activation_status || 'pending').replace('_', ' ')}
                </Badge>
                <Badge className={COMPLIANCE_STYLES[selectedMedic.compliance_status || 'pending']}>
                  {(selectedMedic.compliance_status || 'pending').replace('_', ' ')}
                </Badge>
                {selectedMedic.can_activate ? (
                  <Badge className="bg-[#1ABC9C]/15 text-[#1ABC9C]">Can Activate</Badge>
                ) : (
                  <Badge className="bg-red-500/15 text-red-400">Cannot Activate</Badge>
                )}
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-[#A1A1AA]">Compliance Progress</span>
                  <span className="text-[#F2F2F2]">{selectedMedic.compliance_percentage || 0}%</span>
                </div>
                <Progress value={selectedMedic.compliance_percentage || 0} className="h-3" />
              </div>

              {/* Upload Document Section - Only for users who can upload docs for others */}
              {canUploadDocsForOthers && (
                <div className="border border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-[#F2F2F2] flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Upload Document
                    </h3>
                    <Button
                      size="sm"
                      variant={showUploadForm ? "outline" : "default"}
                      onClick={() => setShowUploadForm(!showUploadForm)}
                      className={showUploadForm ? "border-white/10" : "bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"}
                      data-testid="toggle-upload-form-btn"
                    >
                      {showUploadForm ? (
                        <>
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-1" />
                          Add Document
                        </>
                      )}
                    </Button>
                  </div>

                {showUploadForm && (
                  <div className="space-y-4 pt-3 border-t border-white/10">
                    {/* Document Type Selection */}
                    <div className="space-y-2">
                      <Label>Document Type *</Label>
                      <Select
                        value={uploadForm.document_type_id}
                        onValueChange={handleDocTypeChange}
                      >
                        <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="upload-doc-type-select">
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2B2B2B] border-white/10">
                          {documentTypes.map(type => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                              {type.default_expiry_months && (
                                <span className="text-[#A1A1AA] ml-2 text-xs">
                                  ({type.default_expiry_months} mo validity)
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Expiry Date */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Expiry Date</Label>
                        <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={uploadForm.use_default_expiry}
                            onChange={(e) => {
                              const useDefault = e.target.checked;
                              setUploadForm(prev => ({ ...prev, use_default_expiry: useDefault }));
                              if (useDefault && uploadForm.document_type_id) {
                                handleDocTypeChange(uploadForm.document_type_id);
                              }
                            }}
                            className="rounded"
                          />
                          Use default validity
                        </label>
                      </div>
                      <Input
                        type="date"
                        value={uploadForm.expires_at}
                        onChange={(e) => setUploadForm(prev => ({ 
                          ...prev, 
                          expires_at: e.target.value,
                          use_default_expiry: false 
                        }))}
                        className="bg-[#1E1E1E] border-white/10"
                        data-testid="upload-expiry-input"
                      />
                    </div>

                    {/* File Dropzone */}
                    <div className="space-y-2">
                      <Label>File *</Label>
                      <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                          isDragActive 
                            ? 'border-[#1ABC9C] bg-[#1ABC9C]/10' 
                            : 'border-white/20 hover:border-white/40'
                        }`}
                        data-testid="upload-dropzone"
                      >
                        <input {...getInputProps()} />
                        {uploadFile ? (
                          <div className="flex items-center justify-center gap-2">
                            <FileText className="w-5 h-5 text-[#1ABC9C]" />
                            <span className="text-[#F2F2F2]">{uploadFile.name}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadFile(null);
                              }}
                              className="p-1 h-auto"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        ) : (
                          <div className="py-2">
                            <Upload className="w-8 h-8 mx-auto text-[#A1A1AA] mb-2" />
                            <p className="text-sm text-[#A1A1AA]">
                              {isDragActive ? 'Drop file here' : 'Drag & drop or click to select'}
                            </p>
                            <p className="text-xs text-[#666] mt-1">PDF, JPEG, PNG, DOC or DOCX (max 10MB)</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload Button */}
                    <Button
                      onClick={handleUploadDocument}
                      disabled={uploading || !uploadFile || !uploadForm.document_type_id}
                      className="w-full bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                      data-testid="upload-submit-btn"
                    >
                      {uploading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Document
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
              )}

              <div className="max-h-80 overflow-y-auto">
                <h3 className="text-sm font-medium text-[#A1A1AA] mb-3">Documents</h3>
                {medicDocuments.length === 0 ? (
                  <p className="text-center text-[#A1A1AA] py-4">No documents uploaded</p>
                ) : (
                  <div className="space-y-2">
                    {medicDocuments.map(doc => (
                      <div 
                        key={doc.id}
                        className="p-3 bg-[#1E1E1E] rounded-lg flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[#A1A1AA]" />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-[#F2F2F2] font-medium">{doc.document_type_name}</p>
                              {doc.version > 1 && (
                                <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/20 text-xs">
                                  v{doc.version}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-[#A1A1AA]">
                              {doc.file_name}
                              {doc.expires_at && (
                                <span className="ml-2">
                                  • Expires: {format(parseISO(doc.expires_at), 'dd/MM/yyyy')}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_STYLES[doc.status]}>
                            {doc.status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewDoc(doc)}
                            className="text-[#A1A1AA] hover:text-[#1ABC9C]"
                            title="Preview document"
                            data-testid={`medic-doc-preview-${doc.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewHistory(doc)}
                            className="text-[#A1A1AA] hover:text-[#F2F2F2]"
                            data-testid={`view-history-${doc.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          {canApproveDocuments && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRetypeModal(doc)}
                              className="text-[#A1A1AA] hover:text-[#1ABC9C]"
                              title="Re-categorise document"
                              data-testid={`retype-doc-${doc.id}`}
                            >
                              <Tag className="w-4 h-4" />
                            </Button>
                          )}
                          {doc.status === 'pending' && canApproveDocuments && (
                            <Button 
                              size="sm"
                              onClick={() => {
                                setShowMedicModal(false);
                                openReviewModal({...doc, medic_name: selectedMedic.name});
                              }}
                              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                            >
                              Review
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document History Modal */}
      <DocumentHistoryModal
        open={showHistoryModal}
        onOpenChange={setShowHistoryModal}
        documentHistory={documentHistory}
        loading={loadingHistory}
      />

      {/* Re-categorise Document Modal */}
      <Dialog open={showRetypeModal} onOpenChange={setShowRetypeModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]" data-testid="retype-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#1ABC9C]" />
              Change Document Type
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Move this document into a different category. The file itself stays the same.
            </DialogDescription>
          </DialogHeader>
          {retypeDoc && (
            <div className="space-y-4">
              <div className="p-3 bg-[#1E1E1E] rounded-lg text-sm">
                <p className="text-[#A1A1AA]">File</p>
                <p className="text-[#F2F2F2] font-medium">{retypeDoc.file_name}</p>
                <p className="text-[#A1A1AA] mt-2">Current type</p>
                <p className="text-[#F2F2F2] font-medium">{retypeDoc.document_type_name}</p>
              </div>
              <div className="space-y-2">
                <Label>New document type</Label>
                <Select value={retypeTargetId} onValueChange={setRetypeTargetId}>
                  <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="retype-type-select">
                    <SelectValue placeholder="Select new type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    {documentTypes
                      .filter(t => t.id !== retypeDoc.document_type_id)
                      .map(type => {
                        const alreadyHeld = medicDocuments.some(
                          d => d.id !== retypeDoc.id && d.is_current !== false && d.document_type_id === type.id
                        );
                        return (
                          <SelectItem key={type.id} value={type.id} disabled={alreadyHeld}>
                            {type.name}
                            {alreadyHeld && (
                              <span className="text-[#A1A1AA] ml-2 text-xs">(already on file)</span>
                            )}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowRetypeModal(false)}
                  className="border-white/10"
                  data-testid="retype-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRetypeDocument}
                  disabled={retyping || !retypeTargetId || retypeTargetId === retypeDoc.document_type_id}
                  className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                  data-testid="retype-confirm-btn"
                >
                  {retyping ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Save new type'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inline document preview (iter71.3) */}
      <DocumentPreviewDialog
        open={!!previewDoc}
        onOpenChange={(o) => { if (!o) setPreviewDoc(null); }}
        doc={previewDoc}
      />
    </div>
  );
}
